import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../../../types/Database'

interface TransactionRow {
  id: string
  diveId: string
  payerId: string
  hostId: string
  amount: number
  payerName: string
  hostName: string
  diveName: string
  refund: Database.RefundObject | null
  createdAt: Date
}

type RefundType = 'credit' | 'monetary'

type DiveStatus = Database.Dive['status']

const DIVE_STATUS_COLORS: Record<
  DiveStatus,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  open: 'success',
  full: 'info',
  cancelled: 'warning',
  completed: 'default',
  removed: 'error'
}

interface DiveDetail {
  id: string
  siteName: string
  hostId: string
  description: string
  locationDisplay: string
  dateTimeDisplay: string
  status: DiveStatus
  price: number | null
  maxParticipants: number
  type: 'buddy' | 'guided'
  duration: number
  depthTier: string
  moderationReason: string | null
}

const formatAUD = (amount: number | null): string => {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount)
}

const DisputeHandlingTab = () => {
  const [transactions, setTransactions] = useState<TransactionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Refund dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionRow | null>(null)
  const [refundType, setRefundType] = useState<RefundType | null>(null)
  const [disputeNotes, setDisputeNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  // Success snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  // Dive detail modal state
  const [diveDetailOpen, setDiveDetailOpen] = useState(false)
  const [diveDetailLoading, setDiveDetailLoading] = useState(false)
  const [selectedDiveDetail, setSelectedDiveDetail] =
    useState<DiveDetail | null>(null)
  const [selectedDiveHostName, setSelectedDiveHostName] = useState('')

  const handleDiveClick = async (tx: TransactionRow) => {
    setDiveDetailOpen(true)
    setDiveDetailLoading(true)
    setSelectedDiveHostName(tx.hostName)

    try {
      const db = getFirestore()
      const diveDoc = await getDoc(doc(db, 'dives', tx.diveId))
      if (diveDoc.exists()) {
        const data = diveDoc.data()
        const location = data.location
        const dateTime = data.dateTime
        setSelectedDiveDetail({
          id: tx.diveId,
          siteName: (data.siteName as string) || tx.diveId,
          hostId: data.hostId as string,
          description: data.description as string,
          locationDisplay: location
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
            : '—',
          dateTimeDisplay: dateTime?.toDate?.()
            ? dateTime.toDate().toLocaleString()
            : '—',
          status: data.status as DiveStatus,
          price: (data.price as number | null) ?? null,
          maxParticipants: data.maxParticipants as number,
          type: data.type as 'buddy' | 'guided',
          duration: data.duration as number,
          depthTier: data.depthTier as string,
          moderationReason: (data.moderationReason as string | null) ?? null
        })
      } else {
        setSelectedDiveDetail(null)
      }
    } catch {
      setSelectedDiveDetail(null)
    } finally {
      setDiveDetailLoading(false)
    }
  }

  const fetchTransactions = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const db = getFirestore()
      const transactionsRef = collection(db, 'transactions')
      // Query completed transactions (stripeFee is not null means payment is completed)
      const q = query(transactionsRef, where('stripeFee', '!=', null))
      const snapshot = await getDocs(q)

      const rows: TransactionRow[] = []

      for (const txDoc of snapshot.docs) {
        const data = txDoc.data() as Omit<Database.Transaction, 'id'>

        // Resolve display names for payer and host
        let payerName = data.payerId
        let hostName = data.hostId
        let diveName = data.diveId

        try {
          const payerDoc = await getDoc(doc(db, 'users', data.payerId))
          if (payerDoc.exists()) {
            payerName = payerDoc.data().displayName || data.payerId
          }
        } catch {
          // Keep UID as fallback
        }

        try {
          const hostDoc = await getDoc(doc(db, 'users', data.hostId))
          if (hostDoc.exists()) {
            hostName = hostDoc.data().displayName || data.hostId
          }
        } catch {
          // Keep UID as fallback
        }

        try {
          const diveDoc = await getDoc(doc(db, 'dives', data.diveId))
          if (diveDoc.exists()) {
            diveName = diveDoc.data().siteName || data.diveId
          }
        } catch {
          // Keep diveId as fallback
        }

        rows.push({
          id: txDoc.id,
          diveId: data.diveId,
          payerId: data.payerId,
          hostId: data.hostId,
          amount: data.amount,
          payerName,
          hostName,
          diveName,
          refund: data.refund ?? null,
          createdAt: data.createdAt?.toDate() ?? new Date()
        })
      }

      // Sort by createdAt descending
      rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      setTransactions(rows)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load transactions'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  const handleOpenRefundDialog = (
    transaction: TransactionRow,
    type: RefundType
  ) => {
    setSelectedTransaction(transaction)
    setRefundType(type)
    setDisputeNotes('')
    setDialogError(null)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedTransaction(null)
    setRefundType(null)
    setDisputeNotes('')
    setDialogError(null)
  }

  const handleSubmitRefund = async () => {
    if (!selectedTransaction || !refundType) return

    // Validate dispute notes
    if (disputeNotes.length < 1 || disputeNotes.length > 1000) {
      setDialogError('Dispute notes must be between 1 and 1000 characters.')
      return
    }

    setSubmitting(true)
    setDialogError(null)

    try {
      const functions = getFunctions()

      if (refundType === 'credit') {
        const issueCreditRefund = httpsCallable<
          { transactionId: string; notes: string },
          { success: boolean }
        >(functions, 'issueCreditRefund')

        await issueCreditRefund({
          transactionId: selectedTransaction.id,
          notes: disputeNotes
        })
      } else {
        const issueMonetaryRefund = httpsCallable<
          { transactionId: string; notes: string },
          { success: boolean }
        >(functions, 'issueMonetaryRefund')

        await issueMonetaryRefund({
          transactionId: selectedTransaction.id,
          notes: disputeNotes
        })
      }

      // Success — show confirmation
      const now = new Date()
      const typeLabel =
        refundType === 'credit' ? 'Credit Refund' : 'Monetary Refund'
      setSnackbarMessage(
        `${typeLabel} of $${selectedTransaction.amount.toFixed(
          2
        )} processed at ${now.toLocaleString()}`
      )
      setSnackbarOpen(true)
      handleCloseDialog()

      // Refresh the list
      await fetchTransactions()
    } catch (err: unknown) {
      // Display error details — especially Stripe errors for monetary refunds
      let message = 'Refund failed. Please try again.'
      if (err && typeof err === 'object' && 'message' in err) {
        message = (err as { message: string }).message
      }
      setDialogError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const notesValid = disputeNotes.length >= 1 && disputeNotes.length <= 1000

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant='outlined' onClick={fetchTransactions}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Dispute Handling
      </Typography>

      {transactions.length === 0 ? (
        <Typography color='text.secondary'>
          No completed transactions found.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Dive</TableCell>
                <TableCell>Participant</TableCell>
                <TableCell>Host</TableCell>
                <TableCell align='right'>Amount (AUD)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map(tx => (
                <TableRow key={tx.id}>
                  <TableCell>{tx.createdAt.toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => handleDiveClick(tx)}
                      sx={{ cursor: 'pointer', textAlign: 'left' }}
                    >
                      {tx.diveName}
                    </Link>
                  </TableCell>
                  <TableCell>{tx.payerName}</TableCell>
                  <TableCell>{tx.hostName}</TableCell>
                  <TableCell align='right'>${tx.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    {tx.refund != null ? (
                      <Chip
                        label='Already refunded'
                        color='default'
                        size='small'
                      />
                    ) : (
                      <Chip label='Paid' color='success' size='small' />
                    )}
                  </TableCell>
                  <TableCell>
                    {tx.refund != null ? (
                      <Typography variant='body2' color='text.secondary'>
                        Refund already processed
                      </Typography>
                    ) : (
                      <Box display='flex' gap={1}>
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={() => handleOpenRefundDialog(tx, 'credit')}
                        >
                          Credit Refund
                        </Button>
                        <Button
                          variant='outlined'
                          size='small'
                          color='warning'
                          onClick={() => handleOpenRefundDialog(tx, 'monetary')}
                        >
                          Monetary Refund
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Refund Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {refundType === 'credit' ? 'Credit Refund' : 'Monetary Refund'}
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Participant: {selectedTransaction.payerName}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Amount: ${selectedTransaction.amount.toFixed(2)} AUD
              </Typography>
              {refundType === 'credit' && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1 }}
                >
                  This will add ${selectedTransaction.amount.toFixed(2)} to the
                  participant&apos;s credit balance.
                </Typography>
              )}
              {refundType === 'monetary' && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1 }}
                >
                  This will initiate a Stripe refund to the participant&apos;s
                  original payment method.
                </Typography>
              )}
            </Box>
          )}

          <TextField
            label='Dispute Notes'
            multiline
            rows={4}
            fullWidth
            required
            value={disputeNotes}
            onChange={e => setDisputeNotes(e.target.value)}
            error={disputeNotes.length > 1000}
            helperText={`${disputeNotes.length}/1000 characters (minimum 1)`}
            sx={{ mt: 1 }}
          />

          {dialogError && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitRefund}
            variant='contained'
            color={refundType === 'monetary' ? 'warning' : 'primary'}
            disabled={submitting || !notesValid}
          >
            {submitting ? (
              <CircularProgress size={20} />
            ) : (
              `Confirm ${
                refundType === 'credit' ? 'Credit' : 'Monetary'
              } Refund`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity='success'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dive Detail Modal */}
      <Dialog
        open={diveDetailOpen}
        onClose={() => {
          setDiveDetailOpen(false)
          setSelectedDiveDetail(null)
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Dive Details</DialogTitle>
        <DialogContent>
          {diveDetailLoading && (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          )}
          {!diveDetailLoading && !selectedDiveDetail && (
            <Typography color='text.secondary'>
              Dive details not available.
            </Typography>
          )}
          {!diveDetailLoading && selectedDiveDetail && (
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pt: 1 }}
            >
              <Typography variant='body2'>
                <strong>Title:</strong> {selectedDiveDetail.siteName}
              </Typography>
              <Typography variant='body2'>
                <strong>Host:</strong> {selectedDiveHostName}
              </Typography>
              <Typography variant='body2'>
                <strong>Description:</strong> {selectedDiveDetail.description}
              </Typography>
              <Typography variant='body2'>
                <strong>Location:</strong> {selectedDiveDetail.locationDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Date:</strong> {selectedDiveDetail.dateTimeDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Price:</strong>{' '}
                {selectedDiveDetail.price != null
                  ? formatAUD(selectedDiveDetail.price)
                  : 'Free'}
              </Typography>
              <Typography variant='body2'>
                <strong>Capacity:</strong> {selectedDiveDetail.maxParticipants}
              </Typography>
              <Typography variant='body2'>
                <strong>Type:</strong> {selectedDiveDetail.type}
              </Typography>
              <Typography variant='body2'>
                <strong>Duration:</strong> {selectedDiveDetail.duration} min
              </Typography>
              <Typography variant='body2'>
                <strong>Depth Tier:</strong> {selectedDiveDetail.depthTier}
              </Typography>
              <Typography variant='body2'>
                <strong>Status:</strong>{' '}
                <Chip
                  label={selectedDiveDetail.status}
                  color={DIVE_STATUS_COLORS[selectedDiveDetail.status] ?? 'default'}
                  size='small'
                />
              </Typography>
              {selectedDiveDetail.status === 'removed' &&
                selectedDiveDetail.moderationReason && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Alert severity='warning' sx={{ mt: 1 }}>
                      <strong>Removal reason:</strong>{' '}
                      {selectedDiveDetail.moderationReason}
                    </Alert>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiveDetailOpen(false)
              setSelectedDiveDetail(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DisputeHandlingTab
